/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {FabMessageService} from '@fabric/components/message';
import {
  ColDef,
  GridApi,
  GridReadyEvent, PostSortRowsParams,
  RowSelectionOptions,
  SizeColumnsToContentStrategy,
  SizeColumnsToFitGridStrategy,
  SizeColumnsToFitProvidedWidthStrategy
} from 'ag-grid-community';
import {FCORG_BLACKLIST, GMTVP} from '../../../common/constants';
import {PropulsionScenario} from '../../../models/propulsion-scenario-dto-model';
import {PropulsionScenarioService} from '../../../services/propulsion-scenario-service';
import {AutocompleteFilterComponent} from '../../filters/autocomplete-filter/autocomplete-filter.component';
import {StateService} from '../../state/state.service';

@Component({
    selector: 'app-propulsion-scenario-select',
    templateUrl: './propulsion-scenario-select.component.html',
    styleUrl: './propulsion-scenario-select.component.scss',
    standalone: false
})
export class PropulsionScenarioSelectComponent implements OnInit {
  @Input() titleText: string;
  @Input() validFcorgs: string[] = [];
  @Input() includeTVP: boolean = true;
  @Input() isMultiSelect: boolean = false;
  @Output() confirmEvent = new EventEmitter<any>();

  showSpinner: boolean;
  scenarios: any[] = [];
  displayHiddenScenarios: boolean;
  displayedData: any[] = [];
  selectedScenarios: any[] = [];

  gridApi: GridApi;
  columns: ColDef[] = [
    {field: 'id', headerName: 'Propulsion Scenario ID', filter: AutocompleteFilterComponent},
    {field: 'propulsionScenarioName', headerName: 'Propulsion Scenario Name', filter: AutocompleteFilterComponent},
    {field: 'forecastOrganization', headerName: 'FCORG', filter: AutocompleteFilterComponent},
  ];
  rowSelection: RowSelectionOptions = {
    mode: this.isMultiSelect ? 'multiRow' : 'singleRow',
    checkboxes: false,
    enableClickSelection: true,
    enableSelectionWithoutKeys: true,
    headerCheckbox: false,
  };
  autoSizeStrategy:
    | SizeColumnsToFitGridStrategy
    | SizeColumnsToFitProvidedWidthStrategy
    | SizeColumnsToContentStrategy = {
    type: "fitGridWidth",
  };
  pageSize: number;

  postSortRows: (params: PostSortRowsParams) => void = (
    params: PostSortRowsParams<PropulsionScenario>,
  ) => {
    // Only post sort rows if the user has a primary FCORG preference
    if (this.stateService.userPreferences?.some(pref => pref.preferenceKey === 'Primary FCORG')) {
      const rowNodes = params.nodes;
      // here we put the Primary FCORG rows on top while preserving the sort order if the user has a primary FCORG preference
      let nextInsertPos = 0;
      for (let i = 0; i < rowNodes.length; i++) {
        const fcorg = rowNodes[i].data ? rowNodes[i].data!.forecastOrganization : undefined;
        if (fcorg === this.stateService.userPreferences.find(pref => pref.preferenceKey === 'Primary FCORG')?.preferenceValue) {
          rowNodes.splice(nextInsertPos, 0, rowNodes.splice(i, 1)[0]);
          nextInsertPos++;
        }
      }
    }
  };

  constructor(readonly propulsionScenarioService: PropulsionScenarioService,
              public stateService: StateService,
              public snackBar: FabMessageService) {
  }

  ngOnInit(): void {
    this.pageSize = 100;
    this.displayHiddenScenarios = false;
    this.getPropulsionScenarios();
  }

  /**
   * Gets the grid API and data when the grid is ready
   * @param params the parameters generated by the grid event
   */
  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    this.gridApi.setGridOption("rowSelection", {
      mode: this.isMultiSelect ? 'multiRow' : 'singleRow',
      checkboxes: false,
      enableClickSelection: true,
      enableSelectionWithoutKeys: true,
      headerCheckbox: false,
    });
  }

  /**
   * Fetches the propulsion scenarios and filters them by the input parameters
   */
  getPropulsionScenarios(): void {
    this.showSpinner = true;
    this.propulsionScenarioService.getPropulsionScenarios(this.stateService.getUserGmid()).subscribe(
      (scenarios) => {
        this.scenarios = scenarios.filter(row => !FCORG_BLACKLIST.includes(row.forecastOrganization.toUpperCase()));
        if (this.validFcorgs?.length > 0) {
          this.scenarios = this.scenarios.filter(row => this.validFcorgs.includes(row.forecastOrganization));
        }
        if (!this.includeTVP) {
          this.scenarios = this.scenarios.filter(row => row.forecastOrganization !== GMTVP);
        }
        this.displayedData = this.scenarios.filter(row => row.isHidden === false);
      },
      (error) => {
        this.snackBar.displayError(error);
        this.showSpinner = false;
      },
      () => {
        this.showSpinner = false;
      }
    );
  }

  /**
   * sends data when event emitted
   */
  emitEvent() {
    this.confirmEvent.emit({
      selectedScenarios: this.selectedScenarios,
    });
  }

  /**
   * When the hidden checkbox changes, alter the data displayed in the table
   */
  setTableData(): void {
    this.displayedData = this.scenarios;
    if (!this.displayHiddenScenarios) {
      this.displayedData = this.scenarios.filter(row => row.isHidden === false);
    }
    this.selectedScenarios = [];
    this.emitEvent();
  }

  /**
   * Sets the scenario as selected
   * @param event the event when a scenario is selected
   */
  onSelect(event: any) {
    const row: PropulsionScenario = event.data;
    if (!event.node.selected) {
      this.selectedScenarios = this.selectedScenarios.filter(element => element != row);
    } else {
      if (this.isMultiSelect) {
        this.selectedScenarios.push(row);
      } else {
        this.selectedScenarios = [row];
      }
    }
    this.emitEvent();
  }
}
